import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  storyboardResourcesApi,
  type DialogueVoiceoverResponse,
} from '@/services/api/storyboard-resources';
import { invalidateStoryboardDialogues } from './useStoryboardDialogues';
import { invalidateStoryboardBoardQueries } from './useStoryboardBoard';

const pickActiveOrLatest = (
  voiceovers: DialogueVoiceoverResponse[]
): DialogueVoiceoverResponse | undefined => {
  if (!voiceovers.length) return undefined;
  const active = voiceovers.find((item) => item.isActive);
  if (active) return active;
  return [...voiceovers].sort((a, b) => {
    const aVersion = Number(a.createdAt ? new Date(a.createdAt).getTime() : 0);
    const bVersion = Number(b.createdAt ? new Date(b.createdAt).getTime() : 0);
    return bVersion - aVersion;
  })[0];
};

export const sortDialogueVoiceoversByCreatedAtDesc = (
  voiceovers: DialogueVoiceoverResponse[]
): DialogueVoiceoverResponse[] =>
  [...voiceovers].sort((a, b) => {
    const aCreatedAt = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bCreatedAt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bCreatedAt - aCreatedAt;
  });

export const dialogueVoiceoversKeys = {
  all: ['dialogueVoiceovers'] as const,
  storyboard: (storyboardId: string) =>
    [...dialogueVoiceoversKeys.all, 'storyboard', storyboardId] as const,
};

export function useDialogueVoiceovers(storyboardId: string | null, dialogueIds: string[]) {
  const stableDialogueIds = useMemo(
    () => Array.from(new Set(dialogueIds.filter(Boolean))),
    [dialogueIds]
  );
  const dialogueIdSet = useMemo(() => new Set(stableDialogueIds), [stableDialogueIds]);

  const {
    data: storyboardVoiceovers = [],
    isLoading,
    isFetching,
    isStale,
    isError,
    error,
  } = useQuery({
    queryKey: storyboardId
      ? dialogueVoiceoversKeys.storyboard(storyboardId)
      : [...dialogueVoiceoversKeys.all, 'storyboard', 'none'],
    queryFn: () => storyboardResourcesApi.getStoryboardVoiceovers(storyboardId!),
    // 有 storyboardId 即拉取：避免「空对白 → 首条创建」才 enabled，触发 isLoading 导致整页 bootstrap。
    enabled: Boolean(storyboardId),
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  });

  const voiceoverMap = useMemo(() => {
    const map = new Map<string, DialogueVoiceoverResponse>();
    if (!storyboardVoiceovers.length || !stableDialogueIds.length) return map;

    const groupedByDialogueId = new Map<string, DialogueVoiceoverResponse[]>();
    storyboardVoiceovers.forEach((voiceover) => {
      if (!dialogueIdSet.has(voiceover.dialogueId)) return;
      const list = groupedByDialogueId.get(voiceover.dialogueId);
      if (list) {
        list.push(voiceover);
      } else {
        groupedByDialogueId.set(voiceover.dialogueId, [voiceover]);
      }
    });

    stableDialogueIds.forEach((dialogueId) => {
      const selected = pickActiveOrLatest(groupedByDialogueId.get(dialogueId) ?? []);
      if (selected) map.set(dialogueId, selected);
    });

    return map;
  }, [dialogueIdSet, stableDialogueIds, storyboardVoiceovers]);

  const voiceoversByDialogueId = useMemo(() => {
    const map = new Map<string, DialogueVoiceoverResponse[]>();
    if (!storyboardVoiceovers.length || !stableDialogueIds.length) return map;

    storyboardVoiceovers.forEach((voiceover) => {
      if (!dialogueIdSet.has(voiceover.dialogueId)) return;
      const list = map.get(voiceover.dialogueId);
      if (list) {
        list.push(voiceover);
      } else {
        map.set(voiceover.dialogueId, [voiceover]);
      }
    });

    map.forEach((voiceovers, dialogueId) => {
      map.set(dialogueId, sortDialogueVoiceoversByCreatedAtDesc(voiceovers));
    });

    return map;
  }, [dialogueIdSet, stableDialogueIds, storyboardVoiceovers]);

  return {
    voiceoverMap,
    voiceoversByDialogueId,
    isLoading,
    isFetching,
    isStale,
    isError,
    errors: error ? [error] : [],
  };
}

export function useActivateDialogueVoiceover(_storyboardId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (voiceoverId: string) =>
      storyboardResourcesApi.activateDialogueVoiceover(voiceoverId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: dialogueVoiceoversKeys.storyboard(data.storyboardId),
      });
      invalidateStoryboardDialogues(queryClient, data.storyboardId);
    },
  });
}

export function useDeleteDialogueVoiceover(storyboardId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: string | { voiceoverId: string; projectId?: string | null }) =>
      storyboardResourcesApi.deleteDialogueVoiceover(
        typeof input === 'string' ? input : input.voiceoverId
      ),
    onSuccess: async (_, input) => {
      if (!storyboardId) return;
      const projectId = typeof input === 'string' ? undefined : input.projectId;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: dialogueVoiceoversKeys.storyboard(storyboardId),
        }),
        invalidateStoryboardBoardQueries(queryClient, projectId),
      ]);
      invalidateStoryboardDialogues(queryClient, storyboardId);
    },
  });
}

/**
 * 返回一个函数，用于失活语音生成完成后的相关缓存：
 * 语音覆盖列表、对白列表、分镜面板数据。
 */
export function invalidateDialogueVoiceoversForStoryboard(
  queryClient: QueryClient,
  storyboardId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: dialogueVoiceoversKeys.storyboard(storyboardId),
  });
}

export function useInvalidateVoiceGenerationCache() {
  const queryClient = useQueryClient();
  return useCallback(
    async (storyboardId: string, projectId?: string | null) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: dialogueVoiceoversKeys.storyboard(storyboardId),
        }),
        invalidateStoryboardDialogues(queryClient, storyboardId),
        invalidateStoryboardBoardQueries(queryClient, projectId ?? null),
      ]);
    },
    [queryClient]
  );
}
